import React, {
  memo,
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
  useMemo,
  useCallback,
  forwardRef,
} from 'react';
import { Input, Dropdown, Menu, Tooltip, Modal, Checkbox } from 'antd';
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { deleteConfirm } from '@components/dragableTableFrom/formItems';
import alert from '@components/alert';
import { useScrollEnd } from '@modules/projects/projectDetail/lifeCycle/services/useScrollEnd';
import PendoIO from '@utils/pendo';
import './ag-grid.scss';

// import { SortableContainer, SortableElement } from 'react-sortable-hoc';
// import arrayMove from 'array-move';
//==========================
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable/dist';

const SortableTab = forwardRef(
  (
    {
      id,
      tab,
      editable,
      handleTabClick,
      handleRename,
      handleDelete,
      handleDuplicate,
      handleInputBlur,
      handleTabHide,
      editingIndex,
      activeIndex,
      showDropdown,
      reToggleEditSelf,
      handleEdit,
      componentType,
      index,
      ...props
    },
    ref,
  ) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: transform
        ? `translate(${transform.x}px, ${transform.y}px)`
        : undefined,
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    // Compose refs (setNodeRef + ref from parent)
    const setRefs = (node) => {
      setNodeRef(node);
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
      }
    };

    // If tab is undefined, render nothing
    if (!tab) {
      return null;
    }

    // const isEditing = editingIndex > -1;

    return (
      <div ref={setRefs} style={style} {...attributes} {...listeners}>
        <Tab
          text={tab.text}
          id={tab.id}
          onEdit={() => handleEdit(index)}
          deletable={editable && tab.deletable}
          isHide={tab.display ? false : true}
          onClick={() => handleTabClick(index)}
          onRename={() => handleRename(index)}
          onDelete={() => handleDelete(index)}
          onDuplicate={() => handleDuplicate(index)}
          onInputBlur={() => handleInputBlur(index)}
          onTabHide={() => handleTabHide(index)}
          isEdit={editingIndex === index}
          active={activeIndex === index}
          editable={editable}
          {...props}
        />
      </div>
    );
  },
);

function SortableTabList({
  tabs,
  editable,
  onSortEnd,
  ref,
  tabRef,
  handleTabClick,
  handleRename,
  handleDelete,
  handleDuplicate,
  handleInputBlur,
  handleTabHide,
  editingIndex,
  activeIndex,
  showDropdown,
  reToggleEditSelf,
  handleEdit,
  componentType,
  ...rest
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const [items, setItems] = useState(tabs.map((tab) => tab.id));

  useEffect(() => {
    setItems(tabs.map((tab) => tab.id));
  }, [tabs]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) {
      return;
    } // guard for no drop target
    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newOrder = arrayMove(items, oldIndex, newIndex);
      setItems(newOrder);

      if (onSortEnd) {
        onSortEnd({ oldIndex, newIndex }); // <-- send same signature as old
      }
    }
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDirection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="tab-box">
          {items.map((id, index) => {
            const tab = tabs.find((t) => t.id === id);
            // If tab is not found, don't render anything
            if (!tab) {
              return null;
            }
            return (
              <SortableTab
                key={id}
                id={id}
                tab={tab}
                editable={editable}
                onSortEnd={onSortEnd}
                ref={ref}
                tabRef={tabRef}
                handleTabClick={handleTabClick}
                handleRename={handleRename}
                handleDelete={handleDelete}
                handleDuplicate={handleDuplicate}
                handleInputBlur={handleInputBlur}
                handleTabHide={handleTabHide}
                editingIndex={editingIndex}
                activeIndex={activeIndex}
                showDropdown={showDropdown}
                reToggleEditSelf={reToggleEditSelf}
                handleEdit={handleEdit}
                componentType={componentType}
                index={index}
                {...rest}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}

const InputRefContext = createContext({});
const COPY_PREFIX = 'Copy of ';

const getSortableContainer = () =>
  document.querySelector('.integration-manage-tab-wrapper');

const sortableContainerDefaultProps = {
  distance: 20,
  axis: 'x',
  lockAxis: 'x',
  helperContainer: getSortableContainer,
};

const NEW_TAB_TEXT = 'Tab';

const DropdownInTab = ({
  deletable,
  isHide,
  onDelete,
  onEdit,
  onDuplicate,
  onTabHide,
  componentType,
}) => {
  const onMenuItemClick = ({ key, domEvent }) => {
    console.log('key log :', key);
    switch (key) {
      case 'delete':
        onDelete(key);
        break;
      case 'edit':
        onEdit();
        break;
      case 'duplicate':
        onDuplicate();
        break;
      case 'hide':
        onTabHide(domEvent);
        break;
      default:
        console.error('Unexpected Event', domEvent);
    }
  };

  const actionMenu = (
    <Menu onClick={onMenuItemClick}>
      <Menu.Item key="edit">Edit</Menu.Item>
      <Menu.Item key="duplicate">Duplicate</Menu.Item>
      {deletable && <Menu.Item key="delete">Delete</Menu.Item>}
      {componentType ? undefined : isHide ? (
        <Menu.Item key="hide">Unhide</Menu.Item>
      ) : (
        <Menu.Item key="hide">Hide</Menu.Item>
      )}
    </Menu>
  );
  return (
    <Dropdown
      overlay={actionMenu}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="dropdown-action-menu-1"
    >
      <span className="appkiticon icon-vertical-more-fill" />
    </Dropdown>
  );
};

const maxTabNameLength = 60;

const EditModal = ({
  onCancel,
  onSave,
  onAdd,
  visible = true,
  tabData = {},
  editOrAdd = 'edit',
  groupInfo = [],
}) => {
  const [tabName, setTabName] = useState(tabData?.text || '');
  const [tabPermission, setTabPermission] = useState([]);
  const [pureClientGroupDisabled, setPureClientGroupDisabled] = useState(false);

  const allowedGroupIds = useMemo(
    () =>
      groupInfo
        .filter(
          (groupItem) =>
            groupItem.groupMembers.some((i) => i.type !== 'Client') &&
            groupItem.groupMembers.length,
        )
        .map((groupItem) => groupItem.id),
    [groupInfo],
  );

  const emptyGroupIds = useMemo(
    () =>
      groupInfo
        .filter((groupItem) => groupItem.groupMembers.length === 0)
        .map((groupItem) => groupItem.id),
    [groupInfo],
  );

  useEffect(() => {
    setTabName(tabData?.text || '');
    let nextTabPermission = tabData?.permission || ['client', 'internal'];
    const allowPureClientGroupSelect =
      nextTabPermission.includes('internal') ||
      nextTabPermission.some((i) => allowedGroupIds.includes(i));
    if (!allowPureClientGroupSelect) {
      nextTabPermission = nextTabPermission.filter((i) =>
        allowedGroupIds.includes(i),
      );
    }
    nextTabPermission = nextTabPermission.filter(
      (i) => !emptyGroupIds.includes(i),
    );
    setTabPermission(nextTabPermission);
    setPureClientGroupDisabled(!allowPureClientGroupSelect);
  }, [tabData]);

  const onOk =
    editOrAdd === 'edit'
      ? () => onSave(tabName, tabPermission)
      : () => onAdd({ ...tabData, text: tabName, permission: tabPermission });

  const permissionOptions = useMemo(
    () =>
      groupInfo.map((groupItem) => {
        const isPureClientGroup = groupItem.groupMembers.every(
          (i) => i.type === 'Client',
        );
        const isEmptyGroup = groupItem.groupMembers.length === 0;
        return {
          label: groupItem?.groupName,
          value: groupItem?.id,
          disabled:
            isEmptyGroup || (isPureClientGroup && pureClientGroupDisabled),
        };
      }),
    [groupInfo, pureClientGroupDisabled],
  );

  const onCheckboxChange = useCallback(
    (value) => {
      const allowPureClientGroup =
        value.includes('internal') ||
        value.some((i) => allowedGroupIds.includes(i));
      if (allowPureClientGroup) {
        setTabPermission(value);
      } else {
        setTabPermission(
          value.filter((i) => i !== 'client' && allowedGroupIds.includes(i)),
        );
      }
      setPureClientGroupDisabled(!allowPureClientGroup);
    },
    [allowedGroupIds],
  );

  const options = [
    { label: 'All PwC', value: 'internal' },
    {
      label: 'All Clients',
      value: 'client',
      disabled: pureClientGroupDisabled,
    },
    ...permissionOptions,
  ];

  return (
    <Modal
      maskClosable={false}
      closeIcon={<span className="appkiticon icon-close-fill" />}
      wrapClassName="test-edit-modal-wrapper"
      onCancel={onCancel}
      className="tab-edit-modal"
      visible={visible}
      title={
        <span className="white-list-add-modal-header">
          {editOrAdd === 'edit' ? 'Edit' : 'Add'} Tab
        </span>
      }
      cancelButtonProps={{ className: 'cancel-btn' }}
      okText={editOrAdd ? 'save' : 'add'}
      onOk={onOk}
    >
      <div className="tab-edit-modal">
        <div className="field-name">Tab Name</div>
        <Input
          className="edit-tab-name-input"
          value={tabName}
          onChange={(e) => {
            setTabName(e.target.value);
          }}
          maxLength={maxTabNameLength}
        />
        <div className="field-name permission-field-name">Permission</div>
        <Checkbox.Group
          className="edit-tab-permission-check-box"
          value={tabPermission}
          // options={[
          //   { label: 'Internal', value: 'internal' },
          //   {
          //     label: 'Client',
          //     value: 'client',
          //     disabled: pureClientGroupDisabled,
          //   },
          //   ...permissionOptions,
          // ]}
          onChange={onCheckboxChange}
        >
          {options.map((i) => (
            <Checkbox value={i.value} disabled={i.disabled}>
              <Tooltip title={i.label}>{i.label}</Tooltip>
            </Checkbox>
          ))}
        </Checkbox.Group>
      </div>
    </Modal>
  );
};

const Tab = ({
  active,
  isEdit,
  text,
  isHide,
  className = '',
  onClick,
  onRename,
  onDuplicate,
  onInputBlur,
  onDelete,
  onTabHide,
  deletable = true,
  id,
  showDropdown = true,
  reToggleEditSelf,
  onEdit,
  editable,
  componentType, // if using for project is undefined, if using system setting is project instance ID
}) => {
  const inputRef = useContext(InputRefContext);
  const [inputValue, setInputValue] = useState(text);

  const inputLengthShouldNotExceed50 = (e) => {
    const v = e.target.value;
    let charLimit = maxTabNameLength;
    if (v.substr(0, 8) === COPY_PREFIX) {
      charLimit += 8;
    }
    if (v.length <= charLimit) {
      setInputValue(v);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    alert.warning({
      content: 'The tab name cannot be greater than 50 characters.',
      onOk: reToggleEditSelf,
      onCancel: reToggleEditSelf,
    });
  };

  return (
    <div
      onClick={onClick}
      className={`integration-manage-tab-item ${
        active ? 'is-active' : ''
      } ${className}`}
    >
      {isEdit ? (
        <Input
          ref={inputRef}
          onChange={inputLengthShouldNotExceed50}
          onBlur={onInputBlur}
          value={inputValue}
          onPressEnter={onInputBlur}
          size="small"
          // maxLength={58}
        />
      ) : (
        <Tooltip
          overlayClassName="no-arrow-tooltip"
          title={text}
          getPopupContainer={() => {
            const res =
              document.querySelector('.integration-manage-tab-wrapper') ||
              document.body;
            return res;
          }}
          autoAdjustOverflow={false}
        >
          <div onDoubleClick={onRename} className="tab-text-padding-right">
            <span>{text}</span>
          </div>
        </Tooltip>
      )}
      {showDropdown && isHide && !componentType ? (
        <EyeInvisibleOutlined />
      ) : undefined}
      {showDropdown && !isEdit && editable && (
        <DropdownInTab
          deletable={deletable}
          isHide={isHide}
          onDelete={onDelete}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onTabHide={onTabHide}
          componentType={componentType} // In system setting hide the 'hide' and 'unhide' item
        />
      )}
    </div>
  );
};

const AddIcon = ({ onClick }) => (
  <Tab
    onClick={onClick}
    deletable={false}
    className="add-tab-controler"
    text={<span className="appkiticon icon-plus-fill" />}
    showDropdown={false}
  />
);

function Tabs({
  onChange,
  editable = false,
  currentTab: currentTabInProps,
  groupInfo = [],
  noRender = false,
  tabTriggerAction = () => {},
  componentType, // if using for project is undefined, if using system setting is project instance ID
  ...props
}) {
  const [tabs, changeTabs] = useState(props.tabs || []);
  const [currentTabIndex, toggleTab] = useState(0);
  const [currentEdit, toggleEdit] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const scrollEnd = useScrollEnd(containerRef, currentEdit, 3);
  const [editingTab, setEditingTab] = useState(null);
  const [showEditOrAddModal, setShowEditOrAddModal] = useState('edit');
  const userInfo = useSelector(({ login }) => login.userInfo);
  const projectId = useSelector(({ common }) => common.currentId);

  const handleEdit = (index) => {
    setShowEditOrAddModal('edit');
    setEditingTab(tabs[index]);
  };

  useEffect(() => {
    if (currentEdit > -1 && inputRef) {
      inputRef.current && inputRef.current.focus && inputRef.current.focus();
    }
  }, [currentEdit, inputRef]);

  const currentTabInRedux = useSelector(
    ({ projects: { projectDetail } }) => projectDetail.currentTab,
  );

  const getTabName = (str) => {
    if (typeof str !== 'string') {
      return str;
    }

    if (str.length < 14) {
      return str;
    }
    const subString = str.substr(str.length - 13);
    if (parseInt(subString, 10).toString() !== subString) {
      return str;
    }
    return str.substr(0, str.length - 13);
  };

  const currentTabNameInRedux = getTabName(currentTabInRedux);

  const returnZeroIfNegative = (v) => (v > 0 ? v : 0);

  const scrollToActive = () => {
    const tabRef = document.getElementsByClassName('tab-box')[0];
    const activeTab = tabRef.getElementsByClassName('is-active')[0];
    tabRef.scrollLeft = activeTab.offsetLeft + 3;
  };

  useEffect(() => {
    if (tabs.length === 0) {
      props.clearCardList && props.clearCardList();
      return;
    }

    if (tabs.find((e) => e.id === currentTabInProps)) {
      return;
    }

    trigOnChange({
      index: 0,
      tabs,
      currentTab: tabs[0],
    });
    toggleTab(0);
  }, [tabs]);

  useEffect(() => {
    toggleEdit(currentEdit - 1);
  }, [JSON.stringify(tabs)]);

  useEffect(() => {
    if (!Array.isArray(props.tabs)) {
      return;
    }
    const { isClient, isInternal, email } = userInfo || {};
    let roleFilter = () => true;
    if (!editable) {
      roleFilter = (tab) => {
        if (!Array.isArray(tab.permission) || tab.permission.length === 0) {
          return false;
        }
        // if (tab.permission.length > 1) {
        //   return true;
        // }

        return (
          (tab.permission.includes('client') && isClient) ||
          (tab.permission.includes('internal') && isInternal) ||
          tab.permission.some((key) => {
            const targetGroup = groupInfo?.find(
              (groupItem) => groupItem.id === key,
            );
            if (targetGroup?.groupMembers?.length) {
              return targetGroup?.groupMembers.find(
                (member) =>
                  (isClient &&
                    member.type === 'Client' &&
                    email === member.client.email) ||
                  (isInternal &&
                    member.type === 'Integration' &&
                    email === member.people.email),
              );
            }
            return false;
          })
        );
      };
    }
    const tabsShouldShow = props.tabs.filter(roleFilter);
    changeTabs(tabsShouldShow);
    // onChange({ currentTab: { id: (tabsShouldShow[0] || {}).id } });
  }, [props.tabs, editable, userInfo, JSON.stringify(groupInfo)]);

  useEffect(() => {
    const newIndex =
      returnZeroIfNegative(
        tabs.findIndex((tab) => tab.id === currentTabInProps),
      ) ||
      returnZeroIfNegative(
        props.tabs.findIndex((tab) => tab.text === currentTabNameInRedux),
      );

    if (newIndex > -1 && newIndex !== currentTabIndex) {
      toggleTab(newIndex);
    }
  }, [tabs, currentTabInProps, props.tabs]);

  const trigOnChange = (changedTabs) => {
    typeof onChange === 'function' && onChange(changedTabs);
  };

  const newTabIndex = getNewTabIndex(tabs);

  const handleAdd = (tabIndex) => {
    setShowEditOrAddModal('add');
    setEditingTab(newTab(tabIndex));
  };

  const onAdd = (newTabItem) => {
    const newTabs = [...tabs, newTabItem];
    const newIndex = newTabs.length - 1;

    trigOnChange({
      tabs: newTabs,
      index: newIndex,
      currentTab: newTabItem,
    });

    changeTabs(newTabs);
    toggleTab(newIndex);

    setTimeout(() => {
      scrollToActive();
    }, 0);
  };

  const handleDelete = (index) => {
    if (!editable) {
      return;
    }

    const onOk = () => {
      changeTabs((prevTabs) => {
        const newTabs = prevTabs.filter((_, i) => i !== index);
        const newIndex = Math.min(currentTabIndex, newTabs.length - 1);

        trigOnChange({
          index: newIndex,
          tabs: newTabs,
          currentTab: newTabs[newIndex],
        });
        tabTriggerAction();

        if (newIndex !== currentTabIndex) {
          toggleTab(newIndex);
        }

        setTimeout(scrollToActive, 0);

        return newTabs;
      });
    };

    deleteConfirm({ onOk });
  };

  // const handleDelete = (e, index) => {
  //   e.stopPropagation();
  //   if (!editable) {
  //     return;
  //   }

  //   const onOk = () => {
  //     PendoIO.track('Delete Tab', {
  //       project_id: projectId,
  //       uid: userInfo.uid,
  //       full_name: `${userInfo.given_name} ${userInfo.family_name}`,
  //       tab_name: tabs[index].text,
  //     });
  //     const result = [...tabs].filter((_, i) => i !== index);
  //     let newIndex = currentTabIndex;

  //     if (currentTabIndex > result.length - 1) {
  //       newIndex = result.length - 1;
  //     }

  //     trigOnChange({
  //       index: newIndex,
  //       tabs: result,
  //       currentTab: result[newIndex],
  //     });
  //     tabTriggerAction();

  //     newIndex !== currentTabIndex && toggleTab(newIndex);

  //     changeTabs(result);

  //     setTimeout(() => {
  //       scrollToActive();
  //     }, 0);
  //   };

  //   deleteConfirm({ onOk });
  // };

  const handleDuplicate = (index) => {
    const genCopyOfTab = (i) =>
      Object.assign(
        {},
        tabs[i],
        newTab(
          getNewTabIndex(tabs),
          `${COPY_PREFIX}${tabs[i].text?.slice(
            0,
            maxTabNameLength - COPY_PREFIX.length,
          )}`,
        ),
      );
    const result = [...tabs, genCopyOfTab(index)];

    const newTabIndex = result.length - 1;
    trigOnChange({
      index: newTabIndex,
      tabs: result,
      currentTab: result[newTabIndex],
      duplicateFrom: index,
    });
    tabTriggerAction();
    changeTabs(result);
    setTimeout(() => {
      scrollToActive();
    }, 0);
  };

  const handleTabClick = (index) => {
    if (index === undefined || index === currentTabIndex) {
      return;
    }

    trigOnChange({
      index,
      tabs,
      currentTab: tabs[index],
    });
    toggleTab(index);
  };

  const handleRename = (index) => {
    if (!editable) {
      return;
    }

    toggleEdit(index);
  };

  const handleTabHide = (index) => {
    const newTabs = [...tabs];
    newTabs[index].display = !newTabs[index].display;
    changeTabs(newTabs);
  };

  const saveTag = ({ text, index, permission }) => {
    const newArr = [...tabs];
    newArr[index] = {
      ...newArr[index],
      text,
    };

    if (permission) {
      newArr[index] = {
        ...newArr[index],
        permission,
      };
    }

    trigOnChange({
      tabs: newArr,
      index,
      currentTab: newArr[index],
    });
    tabTriggerAction();
    changeTabs(newArr);
  };

  const handleInputBlur = () => {
    if (currentEdit < 0 || !inputRef.current || !inputRef.current.input.value) {
      return toggleEdit(-1);
    }

    saveTag({
      text: inputRef.current.input.value,
      index: currentEdit,
    });
    toggleEdit(-1);
  };

  const handleTabSort = ({ oldIndex, newIndex }) => {
    const result = arrayMove([...tabs], oldIndex, newIndex);

    const activeTabId = tabs[currentTabIndex].id;

    const newActiveIndex = result.findIndex((item) => item.id === activeTabId);
    changeTabs(result);

    currentTabIndex !== newActiveIndex && toggleTab(newActiveIndex);

    trigOnChange({
      index: newActiveIndex,
      tabs: result,
      currentTab: result[newActiveIndex],
    });
  };

  const handlerScroll = (direction) => {
    const tabRef = document.getElementsByClassName('tab-box')[0];
    const moveWidth = tabRef.clientWidth - 180 - 50 - 130;
    direction === 'right'
      ? (tabRef.scrollLeft += moveWidth)
      : (tabRef.scrollLeft = tabRef.scrollLeft - moveWidth);
  };

  if (noRender) {
    return null;
  }
  return (
    <div className="integration-manage-tab-wrapper">
      <div className="header-box">
        <EditModal
          onCancel={() => setEditingTab(null)}
          onSave={(text, permission) => {
            saveTag({ text, index: currentTabIndex, permission });
            setEditingTab(null);
          }}
          onAdd={(tabItem) => {
            onAdd(tabItem);
            PendoIO.track('Add Tab', {
              project_id: projectId,
              uid: userInfo.uid,
              full_name: `${userInfo.given_name} ${userInfo.family_name}`,
              tab_name: tabItem.text,
            });
            setEditingTab(null);
          }}
          tabData={editingTab}
          visible={Boolean(editingTab)}
          editOrAdd={showEditOrAddModal}
          groupInfo={groupInfo}
        />
        <InputRefContext.Provider value={inputRef}>
          <SortableTabList
            {...sortableContainerDefaultProps}
            tabs={tabs}
            onSortEnd={handleTabSort}
            editable={editable}
            ref={inputRef}
            tabRef={containerRef}
            handleTabClick={handleTabClick}
            handleRename={handleRename}
            handleDelete={handleDelete}
            handleDuplicate={handleDuplicate}
            handleInputBlur={handleInputBlur}
            handleTabHide={handleTabHide}
            editingIndex={currentEdit}
            activeIndex={currentTabIndex}
            showDropdown={editable}
            reToggleEditSelf={() => toggleEdit(currentTabIndex || -1)}
            handleEdit={handleEdit}
            componentType={componentType}
          />
        </InputRefContext.Provider>
        <div className="scroll-control-box">
          {editable && <AddIcon onClick={() => handleAdd(newTabIndex)} />}
          {!scrollEnd.isLeftEnd && (
            <CaretLeftOutlined onClick={() => handlerScroll('left')} />
          )}
          {!scrollEnd.isRightEnd && (
            <CaretRightOutlined onClick={() => handlerScroll('right')} />
          )}
        </div>
      </div>
      <div className="blackline-fixer" />
    </div>
  );
}

Tabs.propTypes = {};

const getNewTabIndex = (tabs = []) => {
  let flag = false;
  const allIndexs = [...tabs]
    .map((item = {}) => Number((item?.text || '').replace(NEW_TAB_TEXT, '')))
    .filter((item) => item > 0)
    .sort((a, b) => a - b);

  const tabIndexs = allIndexs.filter((itm, idx, arr) => {
    if (flag) {
      return false;
    }
    if ((arr[idx + 1] && arr[idx + 1] - itm === 1) || idx === arr.length - 1) {
      return true;
    }
    if (arr[idx + 1] && arr[idx + 1] - itm > 1) {
      flag = true;
      return true;
    }
    return false;
  });
  // if(flag)
  if (tabIndexs.length === 0 || !allIndexs.includes(1)) {
    return 1;
  }

  return tabIndexs[tabIndexs.length - 1] + 1;
};

const newTab = (index, presetTabName) => {
  const tabName = presetTabName || `${NEW_TAB_TEXT}${index}`;
  // const timeStamp = Date.parse(new Date());
  return {
    text: tabName,
    id: uuidv4(),
    deletable: true,
    permission: ['internal', 'client'],
    display: true,
  };
};

export default memo(Tabs);
